/**
 * Searchable employee picker — filters by name (AR/EN) OR employee_number.
 * Reusable across payroll dialogs (adjustment / attendance correction).
 */
import { useState, useMemo } from 'react';
import { Check, CaretUpDown } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export default function EmployeeSearchSelect({
  employees = [],
  value = '',
  onChange,
  language = 'ar',
  direction = 'rtl',
  placeholder,
  disabled = false,
  filterFn,
  testId = 'employee-search',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const isAr = language === 'ar';

  const filtered = useMemo(
    () => (filterFn ? employees.filter(filterFn) : employees),
    [employees, filterFn]
  );
  const selected = filtered.find(e => (e.id || e._id) === value);

  const matches = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return filtered;
    return filtered.filter(e => {
      const num = String(e.employee_number || '').toLowerCase();
      const nameAr = String(e.name || '').toLowerCase();
      const nameEn = String(e.name_en || '').toLowerCase();
      return num.includes(q) || nameAr.includes(q) || nameEn.includes(q);
    });
  }, [filtered, query]);

  const labelOf = (e) => {
    const num = e.employee_number ? `#${e.employee_number}` : '';
    const name = isAr ? (e.name || e.name_en) : (e.name_en || e.name);
    return [num, name].filter(Boolean).join(' · ');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          disabled={disabled}
          data-testid={`${testId}-trigger`}
          dir={direction}
        >
          <span className="truncate">
            {selected ? labelOf(selected) : (placeholder || (isAr ? 'اختر موظفاً…' : 'Select employee…'))}
          </span>
          <CaretUpDown size={16} className="ms-2 shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start" dir={direction}>
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={isAr ? 'ابحث بالرقم الوظيفي أو الاسم…' : 'Search by employee number or name…'}
            value={query}
            onValueChange={setQuery}
            data-testid={`${testId}-input`}
          />
          <CommandList>
            <CommandEmpty>{isAr ? 'لا توجد نتائج' : 'No results'}</CommandEmpty>
            <CommandGroup>
              {matches.slice(0, 100).map((e) => {
                const id = e.id || e._id;
                const num = e.employee_number ? `#${e.employee_number}` : '';
                const label = isAr ? (e.name || e.name_en) : (e.name_en || e.name);
                const isAjeer = !!e.is_contracted_worker;
                return (
                  <CommandItem
                    key={id}
                    value={id}
                    onSelect={() => { onChange(id); setOpen(false); }}
                    data-testid={`${testId}-item-${id}`}
                  >
                    <Check size={14} className={cn('me-2', value === id ? 'opacity-100' : 'opacity-0')} />
                    <span className="text-xs opacity-70 me-2">{num}</span>
                    <span className="flex-1">{label}</span>
                    {isAjeer && (
                      <span
                        className="ms-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 shrink-0"
                        data-testid={`${testId}-ajeer-${id}`}
                      >
                        {isAr ? 'أجير' : 'Ajeer'}
                      </span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
